// E³-Survey 組織結果（Web表示）— 見出し「エンゲージメント ＞ 組織結果」（2026-09 feature/e3viewCreate）
// ------------------------------------------------------------------
// 組織結果PDF（/download30010）を Web 表示に切り替えた画面。デザインはプロトタイプ（v3.2・2026-10-13 第2回の改修検討版）。
// 絞り込み欄の並び（2026-10-24 依頼側の希望で変更）：① 対象（グループ・会社・所属①〜⑦）② 期間（回答期間・タイトル）③ 属性。
// 会社・所属・属性の選択肢はその回答期間の時点の組織情報で決まる（回答期間は既定で締切済みの最新が入る。変えたときは選択を引き継ぐ）。
// 系列会社ありグループ（所属の選択肢の応答が affiliate:true）だけ、① のグループの次で「会社」（系列会社）を選ぶ（所属・属性はその会社の分）。
// 系列会社なしグループは画面・要求本文とも従来どおり（company キーを送らない）。
// 絞り込み欄の一番下に「選択条件の回答状況」と「PDFレポート」（現行の組織結果PDFを同じ条件で作る）を置く。
//
// 集計・権限・6名未満の判定はすべてサーバー（/api/results/30010/...。/org は条件が長いため POST）。
// デザインCSSは .e3r の中だけに効く（styles/e3r.scoped.css・scripts/scopeE3Css.js で生成）。
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Axios from 'axios';
import { useSelector } from 'react-redux';
import { Select, message } from 'antd';
import './styles/e3r.scoped.css';
import './E3Result.css';
import E3Icon from './E3Icon';
import { openReport } from '../../../utils/sessionGuard';
import { EmptyState } from './e3Charts';
import { OverviewTab, ScoreTab, FactorTab, CompareTab, TrendTab, CommentTab } from './E3OrgTabs';
import { companySelectOptions } from '../../../utils/companyDeptSource';
import { companyMetaOf, companyBody, rootAllName } from './e3OrgCompany';
import { keepDeptParts, keepAttrs } from './e3OrgKeep';

const API = '/api/results/30010';
const PATH_SEP = '／';
const ORG_TABS = [
  ['overview', 'オーバービュー'],
  ['score', 'スコア'],
  ['factor', '要因分析'],
  ['compare', '比較'],
  ['trend', '推移'],
  ['comment', 'コメント'],
];
// 属性（変更履歴が残る 4 つ＋性別・年代。雇用形態は改修希望 E3-07 で追加）
const ATTR_DEF = [
  ['position', '役職'],
  ['levelCode', '等級'],
  ['empType', '雇用形態'],
  ['goOut', '出向状態'],
  ['sex', '性別'],
  ['age', '年代'],
];
const EMPTY_ATTRS = { position: [], levelCode: [], empType: [], goOut: [], sex: [], age: [] };
// PDF を出せる状態。回答期間中も現行の組織結果PDFと同じく途中集計のPDFは出せる（Web 表示は締切後）
const PDF_STATUSES = ['ok', 'answering'];

const errorText = (err, fallback) =>
  (err && err.response && err.response.data && err.response.data.error) || fallback;

// 経路一覧から、prefix（'' = ルート）の直下の名前一覧
const childNames = (paths, prefix) => {
  const base = prefix ? prefix.split(PATH_SEP) : [];
  const out = [];
  paths.forEach((p) => {
    const parts = p.split(PATH_SEP);
    if (parts.length <= base.length) return;
    if (base.some((b, i) => parts[i] !== b)) return;
    if (!out.includes(parts[base.length])) out.push(parts[base.length]);
  });
  return out;
};
const joinPath = (parts) => parts.filter(Boolean).join(PATH_SEP);

// 回答期間の表示（JST・「2026/4/15 〜 2026/5/9」）
const jpDate = (v) => (v ? new Date(v).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' }) : '');
const periodOf = (s) => `${jpDate(s.startDate)} 〜 ${jpDate(s.endDate)}`;

// ------------------------------------------------------------------
// 現行の組織結果PDF（POST /api/dbModel100/makeReport30010）の条件へ変換する。
// PDF は「属性ごとに1種類の条件」しか持てないため、表せない組み合わせは null（理由つき）を返す。
const AGE_RANGE = {
  '20代前半（〜24）': [0, 24],
  '20代後半（25〜29）': [25, 29],
  '30代前半（30〜34）': [30, 34],
  '30代後半（35〜39）': [35, 39],
  '40代前半（40〜44）': [40, 44],
  '40代後半（45〜49）': [45, 49],
  '50代前半（50〜54）': [50, 54],
  '50代後半（55〜59）': [55, 59],
  '60代前半（60〜64）': [60, 64],
  '60代後半（65〜）': [65, 120],
};
const AGE_ORDER = Object.keys(AGE_RANGE);
const toPdfSearchInfo = (dept, attrs) => {
  // 現行の組織結果PDFは雇用形態の条件を持てない（改修希望 E3-07 相談1：PDF 側には足さない）
  if (attrs.empType.length) return { reason: '雇用形態の条件はPDFに出力できません' };
  for (const k of ['position', 'levelCode', 'goOut']) {
    if (attrs[k].includes('未設定')) return { reason: '「未設定」を含む条件はPDFに出力できません' };
  }
  if (attrs.sex.length > 1 || attrs.sex.some((v) => v !== '男性' && v !== '女性'))
    return { reason: 'PDFの性別は「男性」か「女性」のどちらか1つだけ指定できます' };
  let ageFrom = 19;
  let ageTo = 75;
  if (attrs.age.length) {
    if (attrs.age.includes('未設定')) return { reason: '年代「未設定」はPDFに出力できません' };
    const idx = attrs.age.map((a) => AGE_ORDER.indexOf(a)).sort((a, b) => a - b);
    if (idx.some((v, i) => i > 0 && v !== idx[i - 1] + 1))
      return { reason: 'PDFの年代は連続した範囲だけ指定できます' };
    ageFrom = AGE_RANGE[AGE_ORDER[idx[0]]][0];
    ageTo = AGE_RANGE[AGE_ORDER[idx[idx.length - 1]]][1];
  }
  return {
    searchInfo: {
      tabs: '1',
      radio01: attrs.position.length ? 1 : 0,
      radio02: attrs.sex.length ? (attrs.sex[0] === '男性' ? 1 : 2) : 0,
      radio03: dept ? 1 : 0,
      radio04: attrs.age.length ? 1 : 0,
      radio05: attrs.levelCode.length ? 1 : 0,
      radio06: attrs.goOut.length ? 1 : 0,
      radio07: ageFrom,
      radio08: ageTo,
      department0: dept,
      position: attrs.position,
      levelCode: attrs.levelCode,
      goOut: attrs.goOut,
    },
  };
};


function E3OrgResultPage() {
  const user = useSelector((state) => state.user && state.user.userData);

  const [groups, setGroups] = useState([]);
  const [fixedGroup, setFixedGroup] = useState(true);
  const [groupCode, setGroupCode] = useState('');
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState('');
  const [opts, setOpts] = useState(null); // 所属・属性の選択肢
  // 系列会社ありグループのときだけ { companies, allowAllCompanies }（系列会社なしは null）。
  // 系列会社を選び直している間（opts が null の間）も Select を出し続けるため、opts とは別に持つ
  const [companyMeta, setCompanyMeta] = useState(null);
  const [company, setCompany] = useState(''); // 選択中の系列会社（'' = 全社（グループ全体））
  const optsSeq = useRef(0); // 所属・属性の選択肢の要求の通し番号（古い応答を捨てる）
  const [deptParts, setDeptParts] = useState([]); // 所属①〜⑦ で選んだ名前
  const [attrs, setAttrs] = useState(EMPTY_ATTRS);
  const [attrRows, setAttrRows] = useState(['']); // ③ 属性の行（1 行に 1 つの属性。'' は未選択の行）
  const [tab, setTab] = useState('overview');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    document.title = 'エンゲージメント 組織結果';
  }, []);

  // ① グループ一覧
  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    Axios.get(`${API}/org/options`)
      .then((res) => {
        if (!active) return;
        setGroups(res.data.groups || []);
        setFixedGroup(!!res.data.fixedGroup);
        if (res.data.groups && res.data.groups.length && (res.data.fixedGroup || res.data.groups.length === 1)) {
          setGroupCode(res.data.groups[0].groupCode);
        }
      })
      .catch((err) => active && setError(errorText(err, 'グループの取得に失敗しました。')));
    return () => {
      active = false;
    };
  }, [user]);

  // ① 回答期間（シーズン）の一覧
  useEffect(() => {
    setSeasons([]);
    setSeasonId('');
    if (!groupCode) return undefined;
    let active = true;
    Axios.get(`${API}/org/options`, { params: { groupCode } })
      .then((res) => {
        if (!active) return;
        const list = res.data.seasons || [];
        setSeasons(list);
        // 既定は締切済みの最新シーズン（無ければ最新）。回答期間中のシーズンは結果が出ないため
        const closed = list.find((x) => x.status === 'closed');
        if (list.length) setSeasonId((closed || list[0]).id);
      })
      .catch((err) => active && setError(errorText(err, 'シーズンの取得に失敗しました。')));
    return () => {
      active = false;
    };
  }, [groupCode]);

  // ①③ 会社・所属・属性の選択肢（回答期間の時点の組織情報で決まる）
  // 系列会社ありグループでは、会社を指定せずに取ると、サーバーが決めた既定の会社（company）の分が返る。
  // 同じグループで回答期間だけを変えたときは、それまでの会社・所属・属性のうち新しい回答期間にあるものを引き継ぐ
  // （2026-10-24 に回答期間を ① の下（②）へ移したため、下を変えると上が消えるのを避ける。e3OrgKeep.js）。
  // グループを変えたときは選び直し
  const selRef = useRef(null); // 直前の描画の選択（回答期間が変わったときの引き継ぎ元）
  selRef.current = { company, deptParts, attrs, attrRows };
  const keepRef = useRef(null); // 引き継ぐ選択（読み込みが終わるまで持つ。続けて回答期間を変えても失わない）
  const loadedGroupRef = useRef(''); // 選択肢を読み込んだグループ
  useEffect(() => {
    if (!keepRef.current && groupCode && loadedGroupRef.current === groupCode) keepRef.current = selRef.current;
    if (loadedGroupRef.current !== groupCode) keepRef.current = null;
    setOpts(null);
    setCompanyMeta(null);
    setCompany('');
    setDeptParts([]);
    setAttrs(EMPTY_ATTRS);
    setAttrRows(['']);
    const seq = ++optsSeq.current;
    if (!groupCode || !seasonId) return undefined;
    let active = true;
    const keep = keepRef.current;
    const isLatest = () => active && seq === optsSeq.current;
    const apply = (data) => {
      if (!isLatest()) return;
      const meta = companyMetaOf(data);
      const co = meta ? data.company || '' : '';
      setCompanyMeta(meta);
      if (meta) setCompany(co);
      if (keep) {
        // 会社が変わった（新しい回答期間に無い）ときは、所属は選び直し
        if (!meta || co === keep.company) setDeptParts(keepDeptParts(keep.deptParts, data.deptPaths));
        const a = keepAttrs(keep.attrs, keep.attrRows, data.attrs);
        setAttrs({ ...EMPTY_ATTRS, ...a.attrs });
        setAttrRows(a.rows);
      }
      setOpts(data); // 選択を入れてから（opts が入ると見られる所属への補正と結果の取得が走る）
      keepRef.current = null;
      loadedGroupRef.current = groupCode;
    };
    Axios.get(`${API}/org/options`, { params: { groupCode, seasonId } })
      .then((res) => {
        if (!isLatest()) return undefined;
        const meta = companyMetaOf(res.data);
        // 引き継ぐ会社が既定の会社と違い、新しい回答期間でも選べるなら、その会社の分を取り直す
        const want = keep && meta ? keep.company : null;
        const canKeep =
          want !== null &&
          want !== (res.data.company || '') &&
          (want === '' ? meta.allowAllCompanies : meta.companies.includes(want));
        if (!canKeep) return apply(res.data);
        return Axios.get(`${API}/org/options`, { params: { groupCode, seasonId, company: want } })
          .then((r2) => apply(r2.data))
          .catch(() => apply(res.data));
      })
      .catch((err) => {
        if (!isLatest()) return;
        keepRef.current = null;
        setError(errorText(err, '所属の取得に失敗しました。'));
      });
    return () => {
      active = false;
    };
  }, [groupCode, seasonId]);

  // ② 系列会社を選び直す（所属・属性はその会社の分を取り直して選び直し）
  const onChangeCompany = (v) => {
    const value = v || '';
    setCompany(value);
    setDeptParts([]);
    setAttrs(EMPTY_ATTRS);
    setAttrRows(['']);
    setOpts(null);
    setResult(null);
    setError('');
    const seq = ++optsSeq.current;
    Axios.get(`${API}/org/options`, { params: { groupCode, seasonId, company: value } })
      .then((res) => {
        if (seq !== optsSeq.current) return; // 後から選んだ会社の応答を優先する
        const meta = companyMetaOf(res.data);
        if (meta) {
          setCompanyMeta(meta);
          setCompany(typeof res.data.company === 'string' ? res.data.company : value);
        }
        setOpts(res.data);
      })
      .catch((err) => seq === optsSeq.current && setError(errorText(err, '所属の取得に失敗しました。')));
  };
  const affiliate = !!companyMeta;

  // ---- 所属（シーズン管理者で部署を割り当てられている人は、割り当てられた範囲だけを選択肢に出す・改修希望 E3-03）
  // サーバーの deptPaths は見られる経路だけ（割り当ての所属とその配下）。上位の所属は経路の途中として選択肢に出るが、
  // そこで止める「すべて」は選べない。見られる所属に行き着くまで、先頭の選択肢を自動で選ぶ
  const deptPaths = useMemo(() => (opts && opts.deptPaths) || [], [opts]);
  const scopes = (opts && opts.deptScopes) || null;
  const canViewPath = useCallback(
    (p) => !scopes || (!!p && scopes.some((s) => p === s || p.startsWith(s + PATH_SEP))),
    [scopes]
  );
  const normalizeParts = useCallback(
    (parts) => {
      let p = parts;
      while (!canViewPath(joinPath(p))) {
        const names = childNames(deptPaths, joinPath(p));
        if (!names.length) break;
        p = [...p, names[0]];
      }
      return p;
    },
    [canViewPath, deptPaths]
  );
  // 引き継いだ所属（e3OrgKeep.js）があれば、そこから見られる所属まで進める
  useEffect(() => {
    if (opts && scopes) setDeptParts((cur) => normalizeParts(cur));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts]);
  const dept = joinPath(deptParts);
  // 割り当てられた所属が、この回答期間の組織表に 1 つも無い（組織の改編など）
  const blocked = !!scopes && opts && !deptPaths.length;

  // 結果の取得（条件が変わるたび）
  const attrKey = JSON.stringify(attrs);
  useEffect(() => {
    if (!opts || !groupCode || !seasonId || blocked) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    Axios.post(`${API}/org`, { groupCode, seasonId, dept, ...attrs, ...companyBody(affiliate, company) })
      .then((res) => active && setResult(res.data))
      .catch((err) => {
        if (!active) return;
        setResult(null);
        setError(errorText(err, '組織結果の取得に失敗しました。'));
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts, groupCode, seasonId, dept, attrKey, affiliate, company]);

  // 集計の条件（回答期間・系列会社・所属・属性）が変わったら、タブをオーバービューに戻す（改修希望 E3-07 の 11）
  useEffect(() => {
    setTab('overview');
  }, [seasonId, company, dept, attrKey]);

  // ---- ③ 属性の行
  const toggleAttr = (k, v) =>
    setAttrs((prev) => ({ ...prev, [k]: prev[k].includes(v) ? prev[k].filter((x) => x !== v) : [...prev[k], v] }));
  const setRowKey = (i, k) => {
    const old = attrRows[i];
    if (old) setAttrs((prev) => ({ ...prev, [old]: [] }));
    setAttrRows(attrRows.map((x, j) => (j === i ? k : x)));
  };
  const delRow = (i) => {
    const old = attrRows[i];
    if (old) setAttrs((prev) => ({ ...prev, [old]: [] }));
    const rows = attrRows.filter((_, j) => j !== i);
    setAttrRows(rows.length ? rows : ['']);
  };
  const clearAttrs = () => {
    setAttrs(EMPTY_ATTRS);
    setAttrRows(['']);
  };
  const usedKeys = attrRows.filter(Boolean);
  const canAddRow = attrRows.every(Boolean) && usedKeys.length < ATTR_DEF.length;
  const attrN = ATTR_DEF.filter(([k]) => attrs[k].length).length;

  const season = seasons.find((s) => s.id === seasonId);
  const group = groups.find((g) => g.groupCode === groupCode);
  // 所属①の「〇〇全体」・比較や推移の「全体」に使う名前（系列会社を選んでいればその会社名）
  const rootName = rootAllName(affiliate, company, group && group.groupName);

  // ---- PDFレポート（現行の組織結果PDFを同じ条件で生成）
  const pdf = useMemo(() => toPdfSearchInfo(dept, attrs), [dept, attrs]);
  const downloadPdf = useCallback(async () => {
    if (!pdf.searchInfo || !season || !group) return;
    setPdfBusy(true);
    message.loading({ content: 'PDFを作成しています…', key: 'e3pdf', duration: 0 });
    try {
      const res = await Axios.post('/api/dbModel100/makeReport30010', {
        // 系列会社ありグループだけ has_affiliates と searchInfo.company を付ける（'' = 全社。サーバーが会社で絞り込む）
        selGroupInfo: {
          group_code: group.groupCode,
          group_name: group.groupName,
          ...(affiliate ? { has_affiliates: true } : {}),
        },
        selSurveySeasonInfo: {
          _id: season.id,
          season_name: season.name,
          survey_start_date: season.startDate,
          survey_end_date: season.endDate,
        },
        searchInfo: { ...pdf.searchInfo, ...companyBody(affiliate, company) },
        userInfo: { role: user && user.role },
      });
      message.success({ content: 'PDFを作成しました', key: 'e3pdf', duration: 2 });
      openReport(res.data.tokenId);
    } catch (err) {
      message.error({ content: errorText(err, 'PDFの作成に失敗しました。'), key: 'e3pdf', duration: 4 });
    } finally {
      setPdfBusy(false);
    }
  }, [pdf, season, group, user, affiliate, company]);

  // ---- ① 回答期間の選択肢（同じ回答期間のシーズンが 2 つ以上あるときだけ、シーズン名を添える）
  const periodCount = seasons.reduce((m, s) => m.set(periodOf(s), (m.get(periodOf(s)) || 0) + 1), new Map());
  const periodOptions = seasons.map((s) => ({
    value: s.id,
    label: `${periodOf(s)}${s.status === 'answering' ? '（回答期間中）' : ''}${
      periodCount.get(periodOf(s)) > 1 ? `　${s.name}` : ''
    }`,
  }));

  // ---- ② 所属のカスケード（所属①〜⑦）
  const cascade = [];
  for (let lv = 0; lv <= deptParts.length && lv < 7; lv += 1) {
    const prefix = joinPath(deptParts.slice(0, lv));
    const names = childNames(deptPaths, prefix);
    if (!names.length) break;
    // 「すべて」（この階層で止める）：割り当ての範囲がある人は、見られる所属のときだけ
    const allOpt = !scopes
      ? [{ value: '', label: lv === 0 ? `${rootName}全体` : 'すべて' }]
      : canViewPath(prefix)
      ? [{ value: '', label: 'すべて' }]
      : [];
    cascade.push(
      <div key={`lv${lv}`} className="as-filter-item">
        <label>{`所属${'①②③④⑤⑥⑦'[lv]}`}</label>
        <Select
          className="e3r-select"
          placeholder="選択してください"
          value={deptParts[lv] || (allOpt.length ? '' : undefined)}
          onChange={(v) => setDeptParts(normalizeParts([...deptParts.slice(0, lv), v].filter(Boolean)))}
          options={[...allOpt, ...names.map((n) => ({ value: n, label: n }))]}
        />
      </div>
    );
  }

  const stepH = (n, txt) => (
    <p className="as-filters-h flt-step">
      <span className="step-no">{n}</span>
      {txt}
    </p>
  );

  // ---- 選択条件の回答状況（どの状態でも API は回答者数・対象者数を返す）
  const sN = result ? (result.node ? result.node.n : result.n) : null;
  const sE = result ? (result.node ? result.node.eligible : result.eligible) : null;
  const sR = sE ? Math.round((sN / sE) * 100) : null;
  const num = (v) => (v == null ? '–' : v.toLocaleString());
  const canPdf = !!pdf.searchInfo && !!season && !pdfBusy && !!result && PDF_STATUSES.includes(result.status);
  const answering = !!season && season.status === 'answering';

  // ---- 結果本体
  // 結果を出す人数（ロールで異なる：シーズン管理者 6 名・ほか制限なし）と、属性別比較の人数。サーバーが決める
  const minN = (result && result.meta && result.meta.minN) || (opts && opts.minN) || 6;
  // コメントのタブは、シーズン管理者には出さない（サーバーが comments を返さない・改修希望 E3-01）
  const tabs = ORG_TABS.filter(([k]) => k !== 'comment' || (result && result.comments));
  const curTab = tabs.some(([k]) => k === tab) ? tab : 'overview';
  let body = null;
  if (blocked)
    body = (
      <div className="as-empty">
        <div className="as-empty-icon">
          <E3Icon name="lock" />
        </div>
        <p className="as-empty-title">
          {affiliate ? 'この会社では、この回答期間に閲覧できる所属がありません' : 'この回答期間には、閲覧できる所属がありません'}
        </p>
        <p className="as-empty-desc">
          {affiliate
            ? `割り当てられた所属が、この回答期間の組織表のこの会社にありません（組織の改編で、名前や位置が変わった場合など）。${
                companyMeta.companies.length > 1 ? 'ほかの会社を選んでください。' : ''
              }`
            : '割り当てられた所属が、この回答期間の組織表にありません（組織の改編で、名前や位置が変わった場合など）。'}
        </p>
      </div>
    );
  else if (error) body = <div className="as-alert warn">{error}</div>;
  else if (!result || (loading && !result)) body = <p className="as-hint">読み込み中…</p>;
  else if (result.status === 'answering') {
    const end = result.meta.season.endDate ? jpDate(result.meta.season.endDate) : null;
    body = (
      <>
        <EmptyState type="B" date={end} rate={sR} />
        <p className="as-hint e3r-answering-pdf">回答期間中の途中集計は、上の「PDFレポート」から確認できます。</p>
      </>
    );
  } else if (result.status === 'empty') body = <EmptyState type="E" />;
  else if (result.status === 'insufficient') body = <EmptyState type="A" n={result.n} minN={minN} />;
  else if (result.status === 'ok') {
    const d = result.node;
    const common = { d, minN, onTab: setTab, groupRef: result.groupRef || null, questions: result.questions };
    // 比較の行を押したとき：その所属・区分に絞り込む（条件が変わるのでタブはオーバービューに戻る・改修希望 E3-11 相談3）
    const drill = (k, v) => {
      if (k === 'org') setDeptParts(normalizeParts([...deptParts, v]));
      else {
        setAttrs((prev) => ({ ...prev, [k]: [v] }));
        setAttrRows((rows) => (rows.includes(k) ? rows : rows.includes('') ? rows.map((x) => (x === '' && !rows.includes(k) ? k : x)) : [...rows, k]));
      }
      window.scrollTo(0, 0);
      message.success(k === 'org' ? `「${v}」の結果を表示しました` : `「${v}」で絞り込みました`);
    };
    // 比較・推移・コメントのタブは、同じ条件でそれぞれの API を呼ぶ
    const query = { groupCode, seasonId, dept, ...attrs, ...companyBody(affiliate, company) };
    const allLabel = `${dept ? `${dept.split(PATH_SEP).slice(-1)[0]} 全体` : `${rootName}全体`}${
      attrN ? '（属性で絞り込み中）' : ''
    }`;
    body = (
      <>
        <div className="as-tabs">
          {tabs.map(([k, l]) => (
            <button key={k} type="button" className={`as-tab ${curTab === k ? 'is-active' : ''}`} onClick={() => setTab(k)}>
              {l}
            </button>
          ))}
        </div>
        <div className={loading ? 'e3r-loading' : undefined}>
          {curTab === 'overview' && <OverviewTab {...common} />}
          {curTab === 'score' && <ScoreTab {...common} />}
          {curTab === 'factor' && <FactorTab {...common} />}
          {curTab === 'compare' && <CompareTab {...common} query={query} allLabel={allLabel} onDrill={drill} />}
          {curTab === 'trend' && <TrendTab {...common} query={query} allLabel={allLabel} />}
          {curTab === 'comment' && <CommentTab {...common} query={query} />}
        </div>
      </>
    );
  }

  return (
    <div className="e3r">
      <div className="content">
        <div className="as-pagehead">
          <div>
            <p className="as-eyebrow">エンゲージメント ＞ 組織結果</p>
            <h1 className="as-title">{season ? `${season.name} 組織結果` : '組織結果'}</h1>
          </div>
        </div>

        <div className="flt-block">
          {stepH(1, affiliate ? '対象を選ぶ（グループ／会社／所属組織）' : '対象を選ぶ（グループ／所属組織）')}
          <div className="as-filters">
            <div className="as-filter-item">
              <label>グループ</label>
              <Select
                className="e3r-select"
                showSearch
                optionFilterProp="label"
                placeholder="選択してください"
                value={groupCode || undefined}
                disabled={fixedGroup}
                onChange={setGroupCode}
                options={groups.map((g) => ({ value: g.groupCode, label: g.groupName }))}
              />
            </div>
            {/* 系列会社ありグループだけ、所属の前に会社（系列会社）を選ぶ（選択肢はサーバーが返す閲覧できる会社） */}
            {affiliate && (
              <div className="as-filter-item">
                <label>会社</label>
                <Select
                  className="e3r-select"
                  showSearch
                  optionFilterProp="label"
                  placeholder="選択してください"
                  value={company || (companyMeta.allowAllCompanies ? '' : undefined)}
                  onChange={onChangeCompany}
                  options={companySelectOptions(companyMeta.companies, { allowAll: companyMeta.allowAllCompanies })}
                />
              </div>
            )}
            {cascade}
          </div>

          {stepH(2, '期間を選ぶ（回答期間／タイトル）')}
          <div className="as-filters">
            <div className="as-filter-item">
              <label>回答期間</label>
              <Select
                className="e3r-select"
                placeholder={groupCode ? '対象の回答期間がありません' : '—'}
                value={seasonId || undefined}
                disabled={!seasons.length}
                onChange={setSeasonId}
                options={periodOptions}
              />
            </div>
            {/* 選んだ回答期間のサーベイシーズン名（表示だけ）。長い名前でも折り返さず、文字の長さに合わせて右へ広がる */}
            <div className="as-filter-item season">
              <label>タイトル</label>
              <div className="season-auto" aria-live="polite">
                {season ? season.name : '—'}
              </div>
            </div>
          </div>

          {stepH(3, '属性で絞り込む（役職／等級／年代／性別など）')}
          <div className="attr-panel">
            {attrRows.map((k, i) => {
              const def = ATTR_DEF.find(([x]) => x === k);
              return (
                <div key={`${i}-${k}`} className="as-filters attr-row">
                  <div className="as-filter-item">
                    <label>{`属性${attrRows.length > 1 ? ` ${i + 1}` : ''}`}</label>
                    <Select
                      className="e3r-select"
                      placeholder="絞り込む属性を選ぶ"
                      value={k || undefined}
                      onChange={(v) => setRowKey(i, v)}
                      options={ATTR_DEF.filter(([x]) => x === k || !usedKeys.includes(x)).map(([x, l]) => ({
                        value: x,
                        label: l,
                      }))}
                    />
                  </div>
                  {def && (
                    <div className="as-filter-item attr-vals">
                      <label>
                        {def[1]}の選択肢<span className="step-sub">複数選べます</span>
                      </label>
                      <div className="as-chips">
                        {((opts && opts.attrs && opts.attrs[k]) || []).map((v) => (
                          <button
                            key={v}
                            type="button"
                            className={`as-chip ${attrs[k].includes(v) ? 'on' : ''}`}
                            aria-pressed={attrs[k].includes(v)}
                            onClick={() => toggleAttr(k, v)}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {(k || attrRows.length > 1) && (
                    <button
                      type="button"
                      className="attr-del"
                      onClick={() => delRow(i)}
                      aria-label={`${def ? def[1] : 'この行'}を外す`}
                      title={`${def ? `${def[1]}の条件` : 'この行'}を外す`}
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
            <div className="attr-foot">
              <button
                type="button"
                className="as-btn ghost sm"
                disabled={!canAddRow}
                title={
                  canAddRow
                    ? ''
                    : usedKeys.length < ATTR_DEF.length
                    ? '先に、空いている行で属性を選んでください'
                    : 'すべての属性を選んでいます'
                }
                onClick={() => setAttrRows([...attrRows, ''])}
              >
                ＋ 属性を追加
              </button>
              {attrN > 0 && (
                <button type="button" className="as-btn ghost sm" onClick={clearAttrs}>
                  条件をクリア
                </button>
              )}
              {usedKeys.length > 0 && (
                <span className="as-hint">
                  同じ属性の中は「いずれか」、属性どうしは「かつ」で絞り込みます。
                  {minN > 1 ? `回答${minN}名未満になる組み合わせは結果を表示しません。` : ''}
                </span>
              )}
            </div>
          </div>

          {/* 選択条件の回答状況と PDFレポート（改修希望 E3-07 の 6・8） */}
          <div className="flt-sum">
            <span className="fs-h">
              選択条件の回答状況{answering && <span className="step-sub">回答期間中（途中の値）</span>}
            </span>
            <span className="fs-i">
              <span className="fs-l">対象者数</span>
              <b>{num(sE)}</b>名
            </span>
            <span className="fs-i">
              <span className="fs-l">回答者数</span>
              <b>{num(sN)}</b>名
            </span>
            <span className="fs-i">
              <span className="fs-l">回答率</span>
              <b>{sR == null ? '–' : sR}</b>%
            </span>
            <span className="fs-pdf">
              <button
                type="button"
                className="as-btn fs-btn"
                onClick={downloadPdf}
                disabled={!canPdf}
                title={pdf.reason || '現在の条件で組織結果のPDFレポートを作成します'}
              >
                <E3Icon name="download" />
                PDFレポート
              </button>
              {pdf.reason && <span className="as-hint">PDF：{pdf.reason}（画面の表示には影響しません）</span>}
            </span>
          </div>
        </div>

        {body}
      </div>
    </div>
  );
}

export default E3OrgResultPage;
